'use client';

import { useEffect, useLayoutEffect, useRef } from 'react';
import { useFundWallet, usePrivy } from '@privy-io/react-auth';
import { polygon } from 'viem/chains';

import { useAuth, useOpenAuthModal, usePolymarketClient, useProxyInitialization } from '@/hooks';

import { USDC_E_CONTRACT_ADDRESS } from '@/constants/tokens.constants';

interface FundWalletBridgeProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Renders nothing; when `isOpen` becomes true it opens Privy's native funding UI
 * (Pay with card, Transfer from exchange, Transfer from wallet, Receive funds).
 *
 * **Dashboard prerequisite:** enable all four funding methods under
 * Privy Dashboard → Account Funding, or the modal will only show toggled-on options.
 * @see https://dashboard.privy.io/apps?page=funding
 */
export function FundWalletBridge({ isOpen, onClose }: FundWalletBridgeProps) {
  const { fundWallet } = useFundWallet();
  const { authenticated: privyAuthenticated, ready } = usePrivy();
  const openAuthModal = useOpenAuthModal();
  const { isAuthenticated, user } = useAuth();
  const { initializeProxy } = useProxyInitialization();
  const { client } = usePolymarketClient();
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });

  // Avoid depending on `user` object identity — useAuth rebuilds it every render.
  const eoaAddress = user?.walletAddress ?? null;
  const proxyFromAuth = user?.proxyWalletAddress ?? null;

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const run = async () => {
      if (!ready) return;

      if (!privyAuthenticated) {
        openAuthModal();
        return;
      }

      if (!isAuthenticated || !eoaAddress) {
        if (!cancelled) onCloseRef.current();
        return;
      }

      if (!client) {
        // Wait for relay client; effect re-runs when `client` becomes available.
        return;
      }

      try {
        // Ensure proxy is deployed and token approvals are in place before funding.
        if (!proxyFromAuth) {
          await initializeProxy(client);
        }
        if (cancelled) return;

        // Fund the EOA, not the proxy Safe.  Smart contract destinations cause gas
        // estimation to revert inside Privy's funding providers ("insufficient funds").
        // The proxy's spending approval over the EOA covers USDC for trading.
        if (!eoaAddress) {
          console.error('[FundWalletBridge] No EOA address available for funding.');
          return;
        }

        await fundWallet({
          address: eoaAddress as `0x${string}`,
          options: { chain: polygon, asset: { erc20: USDC_E_CONTRACT_ADDRESS } },
        });
        if (!cancelled) onCloseRef.current();
      } catch (error) {
        console.error('[FundWalletBridge] Funding flow error:', error);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    ready,
    privyAuthenticated,
    isAuthenticated,
    eoaAddress,
    proxyFromAuth,
    client,
    openAuthModal,
    fundWallet,
    initializeProxy,
  ]);

  return null;
}

export default FundWalletBridge;
